import { ChangeDetectorRef, InjectionToken, TemplateRef } from '@angular/core';

import { VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
import { BehaviorSubject, Subject } from 'rxjs';

import { IconName } from '../../icons-container/icons.type';
import { ItemContext } from './default-templates/list-item/types';
import { SelectedItemContext } from './default-templates/selected-item/types';
import { TriggerContext } from './default-templates/trigger/types';

export interface Select2Item<Value = any> {
  label: string;
  value: Value;
  icon?: IconName;
}

export type SelectFilterFn = (item: Select2Item) => boolean;

export interface HeaderTemplate {
  id: string;
  index: number;
  template: TemplateRef<void>;
}

export type SelectContext = { items: Select2Item[] } | null;

export type SelectItemType<T extends SelectContext> = T extends { items: any[] }
  ? T['items'][number]
  : never;

export interface SelectResolvedTemplate<T = any> {
  templateRef: TemplateRef<T>;
}

export abstract class ItemSelector<Item extends Select2Item> {
  public detectChanges$ = new Subject<void>();
  public selectionChanged$ = new Subject<void>();

  public abstract selected: Item | Item[] | null;
  public abstract setPreselected(items: Item | Item[] | null): void;
  public abstract select(item: Item | Item[]): void;
  public abstract clear(): void;
  public abstract remove(item: Item): void;
  public abstract isSelected(item: Item): boolean;
  public abstract getValue(): Item['value'] | null;
  public abstract setItems(items: Item[]): void;
  public abstract getSelectedItem(): Item | Item[] | null;
  public abstract writeValue(value: Item['value'] | Array<Item['value']>): void;
  public abstract setCompareItemsFn(
    fn: (a: Item, b: Item | { value: Item['value'] }) => boolean,
  ): void;
}

export interface ISelectController<
  Select extends { items: Select2Item[] } | null = {
    items: Select2Item[];
  },
  IS extends ItemSelector<any> = ItemSelector<any>,
> {
  filtersMap: Record<string, SelectFilterFn>;
  headerTemplates: HeaderTemplate[];
  footerTemplates: HeaderTemplate[];
  itemSelector: IS;
  isDisabled$: BehaviorSubject<boolean>;
  isOpen$: BehaviorSubject<boolean>;
  clear$: Subject<void>;
  itemsToRender$: BehaviorSubject<Select2Item[]>;
  select$: Subject<Select2Item | Select2Item[]>;
  keyDown$: Subject<KeyboardEvent>;
  navigation$: BehaviorSubject<{ type: 'item'; index: number }>;
  focusTrigger$: Subject<void>;
  itemsRefresh$: Subject<void>;
  detectChanges$: BehaviorSubject<void>;
  virtualScroll$: BehaviorSubject<VirtualScrollerComponent | null>;
  triggerDirective: SelectResolvedTemplate<TriggerContext<Select>>;
  listItemDirective: SelectResolvedTemplate<ItemContext<Select>>;
  selectedItemDirective: SelectResolvedTemplate<SelectedItemContext<Select>>;
  defaultItems$: BehaviorSubject<Select2Item[]>;
  ngOnDestroy$: Subject<void>;
  cdRef: ChangeDetectorRef;

  setDisabled: (value: boolean) => void;
  setVirtualScroll: (component: VirtualScrollerComponent | undefined) => void;
  toggle: (value?: boolean) => void;
  filterRemove: (name: string) => void;
  filterRegister: (name: string, fn: SelectFilterFn) => void;
  filterItems: (items: Select2Item[]) => Select2Item[];
  itemsRefresh: () => void;
  scrollToIndex: (index: number) => void;
  headerTemplateRegister: (template: HeaderTemplate) => void;
  headerTemplateRemove: (templateId: string) => void;
  footerTemplateRegister: (template: HeaderTemplate) => void;
  footerTemplateRemove: (templateId: string) => void;
  setItems: (items: Select2Item[]) => void;
  isHovered: (itemIndex: number) => boolean;
  getDefaultFilteredItems$: () => any;
  getDefaultItems$: () => any;
}

export const SELECT_CONTROLLER = new InjectionToken<ISelectController>('Select controller');
